import { ImagePaths } from '../../lib/imageUrls.js';

export const copilotGovernanceCompliance = {
  id: 29,
  slug: 'copilot-governance-compliance',
  ogImage: '/og/courses/copilot-governance-compliance.jpg',
  courseCode: 'COPILOT-GOV-001',
  title: 'Microsoft 365 Copilot Governance & Compliance for Australian Organisations',
  subtitle: 'Purview, privacy, and tenant controls for Copilot',
  valueProposition: 'Build the governance and compliance framework required to operate Microsoft 365 Copilot responsibly in Australian organisations, covering Microsoft Purview, tenant controls, APP 8 Cowork obligations, and Work IQ privacy requirements.',
  duration: '12 hours',
  students: 'New',
  level: 'Intermediate',
  organization: 'EDUC4TE',
  image: ImagePaths.COURSE_1,
  shortDescription: 'COPILOT-GOV-001 — Copilot governance and compliance covering Microsoft Purview, APP 8 Cowork, and Work IQ privacy for governance and privacy officers.',
  featured: false,
  price: {
    aud: 799,
    currency: 'AUD',
    earlyBird: 599
  },
  stripeConfig: {
    priceId: 'price_1TTdVyKu1nyc322UlVdxB4c3',
    productId: 'prod_USYdKAfH159JVv'
  },
  snapshot: {
    whoItsFor: [
      'Governance and compliance officers implementing Copilot controls',
      'Microsoft 365 administrators configuring Purview and tenant policies',
      'Privacy officers assessing Work IQ and Cowork obligations',
      'IT directors accountable for Copilot governance in regulated organisations'
    ],
    accreditation: 'Prepares for Microsoft exam AB-900 (Copilot and Agent Administration Fundamentals). Aligned to Privacy Act 1988 APPs, Microsoft Purview, IRAP, PSPF, and ACSC ISM'
  },
  logistics: {
    classSize: 'Maximum 20 participants for hands-on lab exercises',
    requirements: [
      'Completion of Copilot Wave 3 & Agentic AI course or equivalent governance experience',
      'Microsoft 365 administrator access for Purview and tenant configuration exercises',
      'Familiarity with data classification and information protection concepts is helpful',
      'No Purview or compliance portal prior experience required'
    ]
  },
  overview: 'Deploying Microsoft 365 Copilot without a complete governance framework creates regulatory exposure across the Privacy Act 1988, the Protective Security Policy Framework, and the Information Security Manual. This course builds the practical governance and compliance capability that Australian organisations need to operate Copilot Wave 3 responsibly.\n\nAcross 12 hours of instruction, you will configure Microsoft Purview data protection for Copilot, design sensitivity label taxonomies that prevent oversharing in agentic workflows, establish SharePoint and Teams governance prerequisites, and implement Copilot Control System tenant policies. The course dedicates full modules to the two highest-risk Wave 3 features: Copilot Cowork, which routes task execution through Anthropic Claude outside Australian in-country processing guarantees and triggers APP 8 cross-border disclosure obligations; and Work IQ, the persistent employee profiling layer that engages APPs 1, 3, 5, and 11 from the moment an administrator enables it.\n\nParticipants will leave with a completed governance evidence pack — Privacy Impact Assessments, employee disclosure notices, privacy policy update templates, Purview configuration documentation, and tenant control checklists — that satisfies OAIC, APRA, PSPF, and ISM requirements. The course is essential for governance leads, privacy officers, and administrators who are accountable for Copilot compliance in Australian organisations.',
  outcomes: [
    'Configure Microsoft Purview sensitivity labels and DLP policies for Copilot Wave 3 workflows',
    'Implement SharePoint and Teams governance prerequisites before enabling Copilot agents',
    'Apply Copilot Control System tenant policies to restrict agent access and scope',
    'Assess APP 8 cross-border disclosure obligations for Copilot Cowork and Anthropic subprocessor arrangements',
    'Draft a Work IQ Privacy Impact Assessment and employee disclosure notice meeting APP 5 requirements',
    'Update organisational privacy policies to disclose Work IQ collection and agentic uses',
    'Build a governance evidence pack meeting OAIC, APRA, PSPF, and ISM requirements',
    'Communicate Copilot compliance posture to boards, executives, and regulators'
  ],
  audience: [
    'Governance and compliance officers implementing Copilot controls',
    'Microsoft 365 and Purview administrators configuring data protection',
    'Privacy officers in Australian Government agencies and regulated industries',
    'IT directors and security leads accountable for Copilot compliance',
    'Audit and risk professionals reviewing Copilot governance frameworks'
  ],
  prerequisites: [
    'Completion of Copilot Wave 3 & Agentic AI course or equivalent hands-on governance experience',
    'Familiarity with Microsoft 365 administration, including licencing and user management',
    'Basic understanding of data classification and information protection concepts is helpful',
    'No prior Microsoft Purview or compliance portal experience required'
  ],
  modules: [
    {
      title: 'Module 1: Microsoft Purview Data Protection for Copilot',
      duration: '2.5 hours',
      topics: [
        'Sensitivity label taxonomy design for Copilot Wave 3 environments',
        'Auto-labelling policies for SharePoint, OneDrive, and Exchange',
        'DLP policy configuration for Copilot-generated content',
        'Purview Information Protection in the context of agentic AI workflows',
        'Audit and activity explorer for Copilot data handling visibility',
        'Insider risk management signals relevant to Copilot'
      ]
    },
    {
      title: 'Module 2: SharePoint and Teams Governance',
      duration: '2 hours',
      topics: [
        'SharePoint information architecture prerequisites for Copilot agent readiness',
        'External sharing governance and Copilot oversharing risk mitigation',
        'Teams governance: channel policies, guest access, and agent interaction controls',
        'Microsoft 365 Groups and site lifecycle management for Copilot environments',
        'Restricted Content Discovery and Restricted Access Control: configuration and monitoring'
      ]
    },
    {
      title: 'Module 3: Copilot Control System Tenant Policies',
      duration: '1.5 hours',
      topics: [
        'Copilot Control System admin centre configuration: all policy levers',
        'Restricting agent creation, deployment, and discovery by role and group',
        'Connected experiences and optional connected experiences governance',
        'Plugin and extension governance in Wave 3 agentic environments',
        'Audit log retention and eDiscovery scope for Copilot interactions'
      ]
    },
    {
      title: 'Module 4: Copilot Cowork and APP 8 Cross-Border Disclosure',
      duration: '2.5 hours',
      topics: [
        'Cowork task execution: what data moves to Anthropic and when',
        'APP 8 obligations: reasonable steps to ensure overseas recipient compliance',
        'Privacy policy update requirements: disclosing Anthropic as an overseas recipient',
        'Cowork Privacy Impact Assessment: scope, structure, and approval workflow',
        'Configuring Cowork scope controls to limit personal information exposure',
        'Sector obligations: APRA CPS 234, My Health Records Act, legal professional privilege'
      ]
    },
    {
      title: 'Module 5: Work IQ Governance and Employee Privacy',
      duration: '2 hours',
      topics: [
        'Work IQ data categories: role, patterns, habits, relationships — all personal information',
        'APP 1 open and transparent management: privacy policy update requirements',
        'APP 3 necessity test: documenting lawful purpose before enabling Work IQ',
        'APP 5 employee notification: drafting and distributing the disclosure notice',
        'APP 11 security: audit logging, conditional access, and administrative role separation',
        'Government agency PIA requirements under PSPF and ISM'
      ]
    },
    {
      title: 'Module 6: Governance Evidence Pack',
      duration: '1.5 hours',
      topics: [
        'Evidence pack structure: what OAIC, APRA, PSPF, and ISM require',
        'Privacy Impact Assessment sign-off and version control',
        'Tenant configuration documentation and screenshot evidence',
        'Post-activation review cadence: 30, 60, and 90-day checkpoints',
        'Governance maturity assessment and continuous improvement framework'
      ]
    }
  ],
  format: {
    type: 'Live webinar, self-paced online modules, or lunch-and-learn (12 x 1-hour sessions over 12 days)',
    includes: [
      '12 hours of practical instruction across 6 modules',
      'Work IQ Privacy Impact Assessment template',
      'Copilot Cowork APP 8 assessment workbook',
      'Work IQ employee disclosure notice template',
      'Privacy policy update clause library',
      'Governance evidence pack template',
      'Access to recorded sessions for 6 months',
      'Certificate of completion',
      'Email support for 30 days post-course'
    ]
  },
  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Live cohort delivery available on request for teams of 5 or more'
  },
  faqs: [
    {
      question: 'Do I need to complete the Wave 3 course before this one?',
      answer: 'It is strongly recommended. This course assumes you understand Wave 3 agentic capabilities, Work IQ architecture, and Cowork data residency issues covered in the Wave 3 course. If you have equivalent hands-on governance experience with Wave 3 features, you can enrol directly.'
    },
    {
      question: 'Does this course satisfy OAIC and PSPF requirements for deploying Copilot?',
      answer: 'The course teaches you how to complete the governance and documentation work that satisfies OAIC and PSPF requirements. It is not a compliance certification, but participants leave with completed PIA templates, disclosure notices, and evidence pack structures that align with OAIC guidance, PSPF requirements, and ISM controls. Legal review of your specific organisational context is always recommended.'
    },
    {
      question: 'What is the APP 8 issue with Copilot Cowork?',
      answer: 'Copilot Cowork uses Anthropic Claude as a subprocessor for task reasoning. Microsoft Australia in-country processing commitments do not cover the Anthropic-powered components. When Cowork processes personal information, it constitutes a cross-border disclosure under APP 8, requiring the organisation to take reasonable steps to ensure Anthropic does not breach the APPs. Module 4 covers the full assessment and disclosure workflow.'
    },
    {
      question: 'Is this course relevant for APRA-regulated financial services organisations?',
      answer: 'Yes. Module 4 covers APRA CPS 234 obligations for Cowork, including service provider agreement requirements, tiered risk assessment under CPS 230, and APRA notification thresholds. Module 5 covers employee data obligations relevant to all organisations including those subject to APRA prudential standards.'
    },
    {
      question: 'How long does it take to build a governance evidence pack?',
      answer: 'Most organisations can complete the core evidence pack — PIA, disclosure notice, privacy policy update, and tenant configuration documentation — within two to four weeks of completing this course, depending on the complexity of their Copilot deployment and internal approval workflows. Module 6 provides a structured checklist and timeline template.'
    },
    {
      question: 'Can we enrol multiple team members across IT, legal, and privacy functions?',
      answer: 'Yes, and we actively recommend it. Copilot governance requires alignment across IT administration, privacy, legal, and risk functions. Enrolling these teams together ensures the evidence pack is completed with the right expertise and sign-off. For teams of 5 or more, contact us for a private quote.'
    },
    {
      question: 'Will I receive a certificate?',
      answer: 'Yes. Upon completion you will receive a Certificate of Completion from Educ4te confirming your competency in Microsoft 365 Copilot governance and compliance for Australian organisations. This is suitable for CPD records, LinkedIn, and internal governance documentation.'
    },
    {
      question: 'What is the next course after Governance & Compliance?',
      answer: 'The Copilot Security Roadmap: Essential Eight Alignment course is the logical next step, covering all eight Essential Eight controls mapped to Copilot Wave 3, ASD Blueprint ML2 configuration, and Copilot Studio multi-agent governance. The full pathway bundles all four courses at a saving.'
    },
    {
      question: 'Can this course be delivered as lunch-and-learn sessions?',
      answer: 'Yes. The course runs as twelve one-hour sessions over twelve days, with the same content and outcomes as the standard format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on exercises are spread across the sessions, so participants need access to a Microsoft 365 tenant throughout. Contact us to arrange a schedule.'
    }
  ]
};
